import moment from 'moment';
import config from 'src/globals';
import 'moment/locale/pt';
import 'moment/locale/vi';
import { parsePhoneNumber } from 'libphonenumber-js/mobile';
import { startCase, camelCase } from 'lodash';

const { FIREBASE_CONFIG = {}, FIRESTORE_DB_ID } = config;

export function formatDateTime(dateTime, format = 'DD MMM YYYY, hh:mm A', nullOption = 'Not Available') {
  const lng = window.localStorage.getItem('lng') || 'en';
  if (!dateTime) {
    return nullOption;
  }
  const momentDate = moment.isMoment(dateTime) ? dateTime : moment(dateTime);
  return momentDate.isValid() ? momentDate.locale(lng).format(format) : nullOption;
}

export function formatDateTimeFromNow(dateTime, nullOption = 'Not Available') {
  const lng = window.localStorage.getItem('lng') || 'en';
  return dateTime ? moment(dateTime).locale(lng).fromNow() : nullOption;
}

export const getOrgPrettyName = () => {
  const { hostname } = window.location;
  if (hostname?.indexOf('.') > 0) {
    const prettyName = hostname.substring(0, window.location.hostname.indexOf('.'));
    return { origin: hostname, prettyName };
  }
  return { origin: hostname, prettyName: config.DEFAULT_ORG_PRETTY_NAME };
};

export const consignmentTypes = {
  forward: 'Delivery',
  reverse: 'Pickup',
  pickupanddelivery: 'Pickup and Delivery',
  pickupanddeliveryreverse: 'Pickup and Delivery Reverse',
  replacement: 'Replacement',
  hyperlocal: 'Hyperlocal',
};

const consignmentTypesExpress = {
  ...consignmentTypes,
  reverse: 'Reverse',
};

export const consignmentStatus = {
  reachedathub: 'Reached at hub',
  outfordelivery: 'Out for delivery',
  delivered: 'Delivered',
  attempted: 'Attempted',
};

export function consignmentStatusPrettyNames(key) {
  const value = consignmentStatus[key];
  return value || key;
}

export const parseMinuteSeconds = (num) => {
  return `${Math.floor(num / 60)}min ${Math.floor(num % 60)}sec`;
};

export function formatDate(date, nullOption = '---') {
  return date ? moment(date).format('DD MMM, YYYY') : nullOption;
}

export function getConsignmentTypePrettyName(key, isExpress?: any) {
  const value = isExpress ? consignmentTypesExpress[key] : consignmentTypes[key];
  return value || key;
}

export const taskTypes = {
  single_consignment_pickup: 'Single Consignment Pickup',
  delivery: 'Delivery',
  store_consignment_pickup: 'Store Consignment Pickup',
  hubtohub: 'Hub to Hub',
  replacement: 'Replacement',
  rto: 'RTO',
  success: 'Success',
  attempted: 'Attempted',
  unresolved: 'Unresolved',
  created: 'Created',
  arrived: 'Arrived',
  started: 'Started',
  assigned_for_delivery: 'Assigned for Delivery',
};

export const planningTypes = {
  single_consignment_pickup: 'Single Consignment Pickup',
  pickup: 'Pickup Request',
  delivery: 'Delivery',
  job_cod: 'Job COD',
  job_installation: 'Job Installation',
  job_maintenance: 'Job Maintenance',
  job_service: 'Job Service',
  job_inspection: 'Job Inspection',
  job_rectification: 'Job Rectification',
};

export function getTaskTypePrettyName(key) {
  const value = taskTypes[key] || planningTypes[key];
  return value || key;
}

export const getErrorMessage = (err) => {
  let errorMessage = '';
  if (!err.response) {
    errorMessage = 'Could not connect. Please check your internet connection';
  } else {
    const responseData = err.response.data;
    if (responseData && responseData.error) {
      errorMessage = responseData.error.message;
    } else {
      errorMessage = `Something went wrong\n${err.message}`;
    }
  }
  return errorMessage;
};

export const renderTime = (time) => {
  const diff = moment().diff(moment(time), 'seconds');
  if (diff === 0) {
    return 'Just now';
  }
  if (diff > 24 * 3600) {
    return moment.utc(time).local().format('DD-MM-YYYY HH:mm');
  }
  if (diff > 3600) {
    return `${Math.floor(diff / 3600)} hours ago`;
  }
  if (diff > 60) {
    return `${Math.floor(diff / 60)} minutes ago`;
  }
  return `${diff} seconds ago`;
};

export const languageList = [
  {
    key: 'en',
    language_name: 'English',
  },
  {
    key: 'pt',
    language_name: 'Portuguese',
  },
  {
    key: 'vi',
    language_name: 'Vietnamese',
  },
  {
    key: 'ru',
    language_name: 'Russian',
  },
  // {
  //   key: 'hi',
  //   language_name: 'Hindi',
  // },
  // {
  //   key: 'ar',
  //   language_name: 'Arabic',
  // },
];

export const getTimeInWordFormat = (timeInSeconds) => {
  const time = new Date(timeInSeconds.seconds * 1000 + timeInSeconds.nanoseconds / 1e6);
  const currentTime = new Date();
  const timeDifference = currentTime.getTime() - time.getTime();
  const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
  const hoursDifference = Math.floor(
    (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const minutesDifference = Math.floor(
    (timeDifference % (1000 * 60 * 60)) / (1000 * 60),
  );
  let timeInWordFormat = '';
  if (daysDifference >= 1) {
    timeInWordFormat = `${daysDifference} ${daysDifference === 1 ? 'day' : 'days'
      }`;
  } else if (hoursDifference >= 1) {
    timeInWordFormat = `${hoursDifference} ${hoursDifference === 1 ? 'hour' : 'hours'
      }`;
  } else {
    timeInWordFormat = `${minutesDifference} ${minutesDifference === 1 ? 'minute' : 'minutes'
      }`;
  }
  return timeInWordFormat;
};

export const colorsArray = [
  {
    color: '#9254DE',
    backgroundColor: '#F9F0FF',
  },
  {
    color: '#FF7A45',
    backgroundColor: '#FFF2E8',
  },
  {
    color: '#40A9FF',
    backgroundColor: '#E6F7FF',
  },
  {
    color: '#F759AB',
    backgroundColor: '#FFF0F6',
  },
  {
    color: '#52C41A',
    backgroundColor: '#F6FFED',
  },
  {
    color: '#597EF7',
    backgroundColor: '#F0F5FF',
  },
];

export const getHashCode = (string) => {
  let hashCode = 0;
  if (string.length === 0) return hashCode;

  for (let i = 0; i < string.length; i += 1) {
    const char = string.charCodeAt(i);
    hashCode = (hashCode << 5) - hashCode + char; // eslint-disable-line no-bitwise
    hashCode |= 0; // eslint-disable-line no-bitwise
  }

  return hashCode;
};

export const getColor = (string) => {
  // this function will return a color object from Profile Avatars
  const hashCode = getHashCode(string);
  const index = hashCode % colorsArray.length;
  const assignedColorObject = colorsArray[index];

  return assignedColorObject;
};

export const getNameAbbreviation = (fullName) => {
  if (!fullName) return '';
  const words = fullName.split(' ');
  const firstWord = words[0];
  const secondWord = words[1] || '';

  const firstLetter = firstWord.charAt(0);
  const secondLetter = secondWord.charAt(0);
  return `${firstLetter}${secondLetter}`;
};

export const isInAppNotificationAllowed = () => {
  return FIRESTORE_DB_ID && Object.keys(FIREBASE_CONFIG).length !== 0;
};

export const columnsToShowRouteDetails = [
  {
    column_id: 'hub_code',
    pretty_name: 'Hub Code',
  },
  {
    column_id: 'hub_name',
    pretty_name: 'Hub Name',
  },
  {
    column_id: 'distance_from_previous_hub',
    pretty_name: 'Distance (in KM)',
  },
  {
    column_id: 'transit_time',
    pretty_name: 'Transit Time (HH:MM)',
  },
  {
    column_id: 'stoppage_time',
    pretty_name: 'Stoppage Time (HH:MM)',
  },
  {
    column_id: 'distance_variance',
    pretty_name: 'Variance in %',
  },
];
export const downloadFileData = (data, fileName, extensionIncluded: any = false) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${fileName}${extensionIncluded || '.xlsx'}`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const createAndDownloadCSVFileFromJSON = (jsonArray, fileName, keys) => {
  if (!jsonArray?.length) return;
  const headers = keys || Object.keys(jsonArray[0]);

  // Convert each object in the array to a CSV row
  const rows = jsonArray.map((obj) => headers.map((header) => obj[header]?.toString()?.replace(/[\n\t,]/g, ' ') || '').join(','));

  // Add headers as the first row
  rows.unshift(headers.join(','));

  // Join rows with newline character to form CSV string
  const csvString = rows.join('\n');

  // Create a Blob object from the CSV string
  const blob = new Blob([csvString], { type: 'text/csv' });

  // Create a URL for the Blob object
  const url = URL.createObjectURL(blob);

  // Create a download link
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.csv`;

  // Append the link to the document and trigger the download
  document.body.appendChild(link);
  link.click();

  // Clean up by revoking the URL object
  URL.revokeObjectURL(url);
};

export const promiseAllWithConcurrency = async (promiseFns: any[], concurrencyLimit: number) => {
  const results = [];
  const errors = [];
  const executing = [];
  let index = 0;

  const executePromise = async (promiseFn, currentIndex) => {
    try {
      const result = await promiseFn();
      results[currentIndex] = result;
    } catch (error) {
      errors[currentIndex] = error;
    } finally {
      executing.splice(executing.indexOf(currentIndex), 1);
    }
  };

  while (index < promiseFns.length) {
    while (executing.length < concurrencyLimit && index < promiseFns.length) {
      executing.push(index);
      index += 1;
    }

    /* eslint-disable no-await-in-loop */
    await Promise.all(
      executing.map(
        (currentIndex) => executePromise(
          promiseFns[currentIndex],
          currentIndex,
        ),
      ),
    );
  }

  return results;
};

export const isValidMobilePhoneNumber = (phoneNumber) => {
  try {
    const phoneNumberObj = parsePhoneNumber(phoneNumber);
    return phoneNumberObj && phoneNumberObj.isValid();
  } catch (error) {
    return false;
  }
};

export const capitalize = (string: string) => startCase(string);

export const formatNumber = (value: number | string) => {
  const num = Number(value);
  if (Number.isNaN(num)) return value ?? '-';
  return parseFloat(num.toFixed(2)).toString();
};

export const sanitizeDateString = (dateStr: string) => {
  const clean = dateStr.trim().replace(/(?:^"|"$)/g, '').replace(' ', 'T');
  return /(Z|[+-]\d{2}:\d{2})$/.test(clean) ? clean : `${clean}Z`;
};

export const transformName = (name: string) => startCase(camelCase(name));

export const handleXLSXFileWithoutMapping = async (file, firstRowAsHeader?: any): Promise<any> => {
  // To lazy load xlsx module using dynamic import of webpack
  const XLSX = await import(
    /* webpackChunkName: "xlsx" */
    'xlsx'
  );
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    let result = [];
    let fileValue;
    reader.onload = () => {
      fileValue = reader.result;
      const data = new Uint8Array(fileValue as ArrayBuffer);
      const wb = XLSX.read(data, { type: 'array', codepage: 65001 });
      const wsname = wb.SheetNames[0];
      result = XLSX.utils.sheet_to_json(wb.Sheets[wsname], {
        header: firstRowAsHeader ? 0 : 1,
      });
      resolve(result);
    };
    reader.readAsArrayBuffer(file);
  });
};
